import React, { useState, useRef, useEffect } from "react";
import CommonInputContainer from "./CommonInputContainer";
import { Calendar } from "react-date-range";
import moment from "moment";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

/**
 * CommonDatePicker
 *
 * Props:
 * - value: Date | null
 * - onChange: (date: Date | null) => void
 * - placeholder?: string
 * - label?: string
 * - error?: boolean
 * - onFocus?: (e) => void
 * - onBlur?: (e) => void
 * - className?: string
 * - minDate?: Date | null
 * - maxDate?: Date | null
 * - disabledDays?: number[]   // 0 (Sunday) ... 6 (Saturday)
 */
function CommonDatePicker({
  value,
  onChange,
  placeholder,
  label,
  error,
  onFocus,
  onBlur,
  className,
  minDate,
  maxDate,
  disabledDays = [],
  calenderPosition,
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const dropdownRef = useRef(null);

  const isDayDisabledByWeekday = (date) => {
    if (!disabledDays || disabledDays.length === 0) return false;
    const dayIndex = moment(date).day(); // 0..6
    return disabledDays.includes(dayIndex);
  };

  const handleSelect = (date) => {
    // Respect weekday disable rule
    if (isDayDisabledByWeekday(date)) return;
    onChange?.(date);
    setShowCalendar(false);
  };

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatted = value ? moment(value).format("MMM DD, YYYY") : "";

  // Only pass min/max if they exist; otherwise ignore the feature.
  const calendarMinDate = minDate instanceof Date ? minDate : undefined;
  const calendarMaxDate = maxDate instanceof Date ? maxDate : undefined;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <CommonInputContainer
        label={label}
        error={!!error && !value}
        className={className}
      >
        <input
          type="text"
          value={formatted}
          placeholder={placeholder || "Select Date"}
          onFocus={(e) => {
            setShowCalendar(true);
            onFocus && onFocus(e);
          }}
          onBlur={(e) => {
            onBlur && onBlur(e);
          }}
          readOnly
          className={`w-full h-12 items-center px-4 border ${
            error && !value ? "border-red-600" : "border-[#DBDBDB]"
          } rounded-lg placeholder:text-[#A4B1C1] focus:outline-none cursor-pointer`}
        />
      </CommonInputContainer>

      {showCalendar && (
        <div
          className={
            "absolute z-50 mt-2 bg-white rounded-lg shadow-lg" +
            " " +
            calenderPosition
          }
        >
          <Calendar
            date={value || new Date()}
            onChange={handleSelect}
            // Only apply min/max if provided
            minDate={calendarMinDate}
            maxDate={calendarMaxDate}
            color="#3d91ff"
            // Gray out disabled weekdays for clarity
            dayContentRenderer={(day) => {
              const disabled = isDayDisabledByWeekday(day);
              return (
                <span
                  className={`${disabled ? "text-gray-400" : "text-black"}`}
                  style={{
                    opacity: disabled ? 0.5 : 1,
                    pointerEvents: disabled ? "none" : "auto",
                  }}
                >
                  {moment(day).date()}
                </span>
              );
            }}
          />
        </div>
      )}
    </div>
  );
}

export default CommonDatePicker;
